import { Component, forwardRef, input, output, viewChild } from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { InputNumber, InputNumberInputEvent } from "primeng/inputnumber";
import { CustomInput } from "@kernel/form/classes/custom-input";
import { TranslatePipe } from "@ngx-translate/core";
import { FormErrorMessageComponent } from "../../../../tools/form-error-message/components/form-error-message/form-error-message.component";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconComponent } from "@kernel/form/modules/tools/icon-field/components/input-icon/input-icon.component";
import { IIconConfig } from "@kernel/shared/interfaces/icon-config.interface";
import { NgStyle } from "@angular/common";

@Component({
  selector: "sxw-number-input",
  templateUrl: "./number-input.component.html",
  styleUrls: ["./number-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
  ],
  imports: [FormsModule, TranslatePipe, FormErrorMessageComponent, InputNumber, IconFieldModule, InputIconComponent, NgStyle],
})
export class NumberInputComponent extends CustomInput<number> {
  stepValue = input(1);
  label = input<string | null>();
  maxValue = input(100000000);
  minValue = input(0);
  modeType = input("decimal"); // decimal, currency...
  localType = input("it-IT"); // en-IN, de-DE, jp-JP...
  currencyType = input("EUR"); // USD, EUR, INR, CNY, JPY...
  currencyDisplay = input("symbol"); // symbol, code
  enableShowButtons = input(false);
  enableButtonLayout = input("horizontal"); // horizontal, vertical
  useGrouping = input(true);
  placeholder = input("");
  suffix = input("");
  prefix = input("");
  maxLength = input<number | null>(null);
  minFractionDigits = input(0);
  maxFractionDigits = input<number | null>(null);
  size = input<"small" | "large">("small");
  dt = input<Record<string, unknown> | undefined>(undefined);
  theme = input("");
  keyPressEventCodeOrKey = input<number | string>(13); // enter
  leftIconConfig = input<IIconConfig>();
  rightIconConfig = input<IIconConfig>();

  change = output<string | number | null>();
  keyPressChange = output<number | null>();
  blurChange = output<number | null>();

  numberInput = viewChild<InputNumber>("numberInput");

  onInputChanged(event: InputNumberInputEvent) {
    this.numberValueChange(event.value);
    this.change.emit(event.value);
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.keyPressEventCodeOrKey() && (event.keyCode === this.keyPressEventCodeOrKey() || event.key === this.keyPressEventCodeOrKey())) {
      this.numberValueChange(this.value ?? null);
      return this.keyPressChange.emit(Number(this.value) || null);
    } else if (!this.keyPressEventCodeOrKey()) {
      this.numberValueChange(this.value ?? null);
      this.keyPressChange.emit(Number(this.value) || null);
    }
  }

  numberValueChange(value: string | number | null) {
    this.onTouched();
    this.onChange(value == null || isNaN(+value) ? null : Math.min(Math.max(+value, this.minValue()), this.maxValue()));
  }

  onBlur(_: Event) {
    this.onTouched();
    this.blurChange.emit(this.value || null);
  }

  override get summaryValue(): string {
    if (this.value === undefined || this.value === null) {
      return "";
    } // when value is equal to 0, it's true
    return this.modeType() === "decimal" ? `${this.value}` : `${this.numberInput()?.prefixChar} ${this.value}`;
  }
}
